import React, { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import StoryblokComponent from 'storyblok-js-client';
import { useQueryParamString } from 'react-use-query-param-string';

import { DynamicTeaser } from './dynamic-teaser';
import { CategoriesList } from './categories-list';
import { Props } from '../types';
import { blokToComponent } from '../helpers';

const overviewLayoutsMap = {
  blog: '12-12',
  did: '9-3',
  default: '12-12',
};

export const ArticlesOverview = (
  { blok, getComponent, slot }: Props<StoryblokComponent>,
): ReactElement => {
  const pageType = (blok.articles || [])[0]?.page_type;
  const layout = overviewLayoutsMap[pageType] || overviewLayoutsMap.default;

  const [categoryQueryParam, setCategoryQueryParam] = useQueryParamString('categories', '');
  const [authorQueryParam, setAuthorQueryParam] = useQueryParamString('authors', '');

  const resetLink = useRef<HTMLDivElement | null>(null);

  const [queryObj, setQueryObj] = useState({});

  useEffect(() => {
    setQueryObj({
      categories: categoryQueryParam || null,
      authors: authorQueryParam || null,
    });
  }, [categoryQueryParam, authorQueryParam]);

  const handleCategoryChange = (e): void => {
    const { identifier } = e.detail;
    setCategoryQueryParam(identifier);
  };

  const handleAuthorsChange = (e): void => {
    const { identifier } = e.detail;
    setAuthorQueryParam(identifier);
  };

  const resetFilter = (e): void => {
    e.detail?.event.preventDefault();
    setCategoryQueryParam('');
    setAuthorQueryParam('');
  };

  useEffect(() => {
    document.addEventListener('rocheCategoryToggle', handleCategoryChange);
    document.addEventListener('rocheAuthorChange', handleAuthorsChange);
    resetLink.current?.addEventListener('roche-generic-link-click', resetFilter);

    return (): void => {
      document.removeEventListener('rocheCategoryToggle', handleCategoryChange);
      document.removeEventListener('rocheAuthorChange', handleAuthorsChange);
      resetLink.current?.removeEventListener('roche-generic-link-click', resetFilter);
    };
  }, []);

  const renderTeaserList = (extraProps = {}) => blok.articles && blok.articles[0]
    && <DynamicTeaser
      blok={blok.articles[0]}
      getComponent={getComponent}
      filterSettings={queryObj}
      {...extraProps}
    />;

  const renderCategoryList = (extraProps = {}) => blok.categories && blok.categories[0]
    && <CategoriesList
      blok={blok.categories[0]}
      getComponent={getComponent}
      activeCategory={categoryQueryParam}
      {...extraProps}
    />;

  const renderResetButton = () => blok.reset_filter_button
    && blok.reset_filter_button[0]
    && <div ref={resetLink}>
      {blokToComponent({ blok: blok.reset_filter_button[0], getComponent }, 'generic-link')}
    </div>;

  if (layout === '9-3') {
    return (
      <roche-grid layout={'3-9'} slot={slot}>
        {renderTeaserList({ slot: 'right' })}
        <div slot="left">
          {renderCategoryList()}
          {renderResetButton()}
        </div>
      </roche-grid>
    );
  }

  return (
    <roche-grid layout={'12'} slot={slot}>
      {renderCategoryList()}
      {renderResetButton()}
      {renderTeaserList()}
    </roche-grid>
  );
};
